import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useCreateCollaboration, usePatientSearch } from '../hooks/useCollaborationQueries';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

export function CreateCollaborationDialog({ open, doctors, onClose, onCreated }) {
  const dialog = useRef(null);
  const [patientQuery, setPatientQuery] = useState('');
  const [patient, setPatient] = useState(null);
  const [reason, setReason] = useState('');
  const debouncedPatient = useDebouncedValue(patientQuery.trim());
  const patients = usePatientSearch(debouncedPatient);
  const create = useCreateCollaboration();

  useEffect(() => {
    if (open && !dialog.current?.open) dialog.current?.showModal();
    if (!open && dialog.current?.open) dialog.current?.close();
  }, [open]);

  const close = () => {
    dialog.current?.close();
    setPatient(null);
    setPatientQuery('');
    setReason('');
    onClose();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!patient || doctors.length === 0) return;
    try {
      const response = await create.mutateAsync({
        patientId: patient.id,
        reason: reason.trim(),
        invitedDoctorIds: doctors.map((doctor) => doctor.id),
      });
      toast.success('Collaboration invitations sent.');
      onCreated?.(response?.data?.data?.id);
      close();
    } catch (error) {
      toast.error(error?.message || 'Unable to create the collaboration.');
    }
  };

  return (
    <dialog ref={dialog} className="collab-dialog" onCancel={(event) => { event.preventDefault(); close(); }}>
      <form onSubmit={submit}>
        <header><div><p className="collab-eyebrow">New shared workspace</p><h2>Start a collaboration</h2></div><button type="button" aria-label="Close" onClick={close}><X /></button></header>
        <div className="collab-dialog__body">
          <label className="collab-field"><span>Patient</span>
            {patient ? <button className="collab-patient-picked" type="button" onClick={() => setPatient(null)}><span className="collab-patient-name">{patient.name}</span><small className="collab-patient-meta">{patient.identifier || patient.phone}</small><X size={14} /></button> : (
              <><div className="collab-input"><Search size={16} /><input value={patientQuery} onChange={(event) => setPatientQuery(event.target.value)} placeholder="Search your patients" autoFocus /></div>
              {patients.isFetching && <span className="collab-field__hint">Searching patients…</span>}
              {patients.isError && <span className="collab-field__hint collab-field__hint--error">Unable to search patients.</span>}
              {!patients.isFetching && debouncedPatient.length >= 2 && patients.data?.items?.length === 0 && <span className="collab-field__hint">No matching patients.</span>}
              {patients.data?.items?.length > 0 && <div className="collab-patient-results">{patients.data.items.map((item) => <button type="button" key={item.id} onClick={() => setPatient(item)}><span className="collab-patient-name">{item.name}</span><small className="collab-patient-meta">{[item.age && `${item.age} years`, item.identifier, item.phone].filter(Boolean).join(' · ')}</small></button>)}</div>}</>
            )}
          </label>
          <label className="collab-field"><span>Reason for collaboration</span><textarea required minLength={5} maxLength={500} rows={4} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Give the invited care team the clinical context they need." /></label>
          <div className="collab-field"><span>Invited doctors ({doctors.length})</span><div className="collab-invitees">{doctors.map((doctor) => <span key={doctor.id}>{doctor.name}</span>)}</div></div>
        </div>
        <footer><button type="button" className="collab-button collab-button--ghost" onClick={close}>Cancel</button><button className="collab-button collab-button--primary" disabled={!patient || doctors.length === 0 || reason.trim().length < 5 || create.isPending}>{create.isPending ? 'Creating…' : 'Create & invite'}</button></footer>
      </form>
    </dialog>
  );
}

export default CreateCollaborationDialog;
